import { useEffect, useState } from 'react';
import { IDocumentSpec } from 'app/services/rtc/sync/DocumentSpec';
import { IActionHandler } from 'app/services/rtc/sync/documentmanagers/lww';
import { useRTC } from 'app/containers/rtc/Provider';
import { useRtcStateStore, getIsJoined } from 'app/containers/rtc/RtcStateContext';
import { Stream, IEncodedStreamActionTypeMap } from '@engoo/realtime-protocol/stream';
import { SyncState } from 'app/models/rtc/sync/constants';
import {
  Events,
  IChangedEvent,
  IRemoteUpdatedEvent,
  IMetadataChangedEvent,
  IDocumentManager,
} from 'app/services/rtc/sync/documentmanagers';
import { IMetadata } from 'app/services/rtc/sync/ActionStream';
import { bindEvents, unbindEvents } from 'app/utils/events';
import { useMemoOne } from 'app/utils/useMemoOne';

export interface IDocumentManagerState<
  DocumentT, ManagerT
> {
  readonly documentState: DocumentT | undefined;
  readonly metadata: IMetadata | undefined;
  readonly syncState: SyncState;
  readonly manager: ManagerT | undefined;
  readonly isBlocked: boolean;
}

/**
 * WARNING: this hook is not supposed to be used anywhere outside
 * top-level document context providers. Use domain-specific
 * hooks defined in said providers to fetch the context value
 */
const useDocumentManager = <
  StreamName extends Stream,
  DocumentT,
  ActionHandlerT extends IActionHandler<DocumentT, IEncodedStreamActionTypeMap[StreamName]>,
  ManagerT extends IDocumentManager<DocumentT, IEncodedStreamActionTypeMap[StreamName]>
>(
  streamName: StreamName,
  spec: IDocumentSpec<DocumentT, IEncodedStreamActionTypeMap[StreamName], ActionHandlerT, ManagerT>,
) => {
  const rtc = useRTC();
  const rtcStateStore = useRtcStateStore();
  const isJoined = getIsJoined(rtcStateStore);

  const [documentState, setDocumentState] = useState<DocumentT | undefined>(undefined);
  const [metadata, setMetadata] = useState<IMetadata | undefined>(undefined);
  const [syncState, setSyncState] = useState<SyncState>(SyncState.closed);

  const manager = useMemoOne(() => {
    if (!rtc || !isJoined) {
      return;
    }

    const { managerType, actionHandler, initialState } = spec;
    const actionStream = rtc.getActionStream(streamName);

    return new managerType(
      actionHandler,
      actionStream,
      initialState,
      actionStream.metadata,
    );
  }, [rtc, isJoined, spec, streamName]);

  useEffect(() => {
    if (!manager) return;

    const eventHandlers = {
      [Events.OPENED]: () => {
        setSyncState(manager.status);
      },
      [Events.CLOSED]: () => {
        setSyncState(manager.status);
      },
      [Events.SYNCED]: () => {
        setSyncState(manager.status);
      },
      [Events.CHANGED]: (e: IChangedEvent<DocumentT, IEncodedStreamActionTypeMap[StreamName]>) => {
        setDocumentState(e.state);
      },
      [Events.REMOTE_UPDATED]: (e: IRemoteUpdatedEvent<DocumentT, IEncodedStreamActionTypeMap[StreamName]>) => {
        setDocumentState(e.state);
      },
      [Events.METADATA_CHANGED]: (e: IMetadataChangedEvent) => {
        setMetadata(e);
      },
    };

    bindEvents(manager, eventHandlers);
    setDocumentState(spec.initialState);
    manager.start();

    return () => {
      manager.shutdown();
      unbindEvents(manager, eventHandlers);

      setDocumentState(undefined);
      setMetadata(undefined);
      setSyncState(SyncState.closed);
    };
  }, [manager, spec]);

  const isBlocked = (
    syncState !== SyncState.synced ||
    (metadata?.blockedReasons.length ?? 0) > 0
  );

  return {
    documentState,
    syncState,
    metadata,
    manager,
    isBlocked,
  };
};

export default useDocumentManager;
